import { Hand, MapPin, MessageSquareText, MessagesSquare, RotateCcw } from "lucide-react";
import { Pill } from "@/components/Pill";
import { NameWithAvatar } from "@/components/Avatar";
import { ALARM_LEVELS, fmtDate } from "@/lib/constants";

const alarmRow = (t) => ALARM_LEVELS.includes(t.escalation_level) && t.status !== "Resolved";

function AssignButton({ ticket, user, onAssign }) {
  if (ticket.assigned_to_id) {
    return (
      <span className="text-sm font-semibold text-zinc-700" data-testid={`assigned-to-${ticket.ticket_id}`}>
        <NameWithAvatar id={ticket.assigned_to_id} name={ticket.assigned_to} size={22}>{ticket.assigned_to_id === user.id ? "You" : ticket.assigned_to}</NameWithAvatar>
      </span>
    );
  }
  return (
    <button
      className="btn-primary !px-3 !py-1.5 !text-sm"
      onClick={(e) => {
        e.stopPropagation();
        onAssign(ticket);
      }}
      data-testid={`assign-to-me-button-${ticket.ticket_id}`}
    >
      <Hand className="h-4 w-4" /> Assign to me
    </button>
  );
}

export function TicketTable({ tickets, user, onOpen, onAssign, loading, selectable = false, selected, onToggle, onToggleAll }) {
  if (loading) return <p className="py-10 text-center text-zinc-600" data-testid="tickets-loading">Loading tickets…</p>;
  if (!tickets.length)
    return (
      <div className="rounded-xl border-2 border-dashed border-zinc-400 bg-white py-14 text-center" data-testid="tickets-empty">
        <p className="font-display text-xl font-bold">No tickets here</p>
        <p className="mt-1 text-sm text-zinc-600">Try clearing the filters, or enjoy the quiet.</p>
      </div>
    );
  const pickable = tickets.filter((t) => !t.assigned_to_id && t.status !== "Resolved");
  const allPicked = pickable.length > 0 && pickable.every((t) => selected?.has(t.id));
  const stop = (e) => e.stopPropagation();
  const box = (t) =>
    selectable &&
    (!t.assigned_to_id && t.status !== "Resolved" ? (
      <input type="checkbox" className="h-5 w-5 accent-black" checked={selected?.has(t.id) || false} onClick={stop} onChange={() => onToggle(t.id)} aria-label={`Select ${t.ticket_id}`} data-testid={`select-ticket-${t.ticket_id}`} />
    ) : (
      <span className="inline-block h-5 w-5" />
    ));

  return (
    <>
      <div className="hidden overflow-x-auto rounded-xl border-2 border-black bg-white md:block">
        <table className="w-full text-left text-sm" data-testid="tickets-table">
          <thead className="bg-black text-[11px] uppercase tracking-wider text-white">
            <tr>
              {selectable && (
                <th className="px-3 py-3">
                  <input type="checkbox" className="h-5 w-5 accent-[#FACC15]" checked={allPicked} disabled={!pickable.length} onChange={() => onToggleAll(pickable.map((t) => t.id), !allPicked)} aria-label="Select all unassigned on this page" data-testid="select-all-unassigned" />
                </th>
              )}
              <th className="px-4 py-3">Ticket</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Product · Query</th>
              <th className="px-4 py-3">QRC</th>
              <th className="px-4 py-3">Priority</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">SLA</th>
              <th className="px-4 py-3">Assigned</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {tickets.map((t) => (
              <tr
                key={t.id}
                onClick={() => onOpen(t)}
                className={`cursor-pointer transition-colors hover:bg-[#FEF9C3] ${alarmRow(t) ? "bg-[#FEF2F2] border-l-4 border-l-[#DC2626]" : ""} ${selected?.has(t.id) ? "bg-[#FEF9C3]" : ""}`}
                data-testid={`ticket-row-${t.ticket_id}`}
              >
                {selectable && <td className="px-3 py-3" onClick={stop}>{box(t)}</td>}
                <td className="px-4 py-3">
                  <span className="font-mono font-bold">{t.ticket_id}</span>
                  {t.resubmit_count > 0 && (
                    <span className="ml-1.5 inline-flex items-center rounded-full border border-black bg-white px-1.5 py-0.5 text-[10px] font-bold" title="Customer re-submitted the same query" data-testid={`resubmit-${t.ticket_id}`}>
                      re-submitted {t.resubmit_count}×
                    </span>
                  )}
                  {t.repeat_issue && (
                    <span className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-[#F97316] px-1.5 py-0.5 text-[10px] font-bold text-white" title={`Reopened by customer after resolution${t.reopened_from ? ` (resolved by ${t.reopened_from})` : ""}`} data-testid={`repeat-issue-${t.ticket_id}`}>
                      <RotateCcw className="h-3 w-3" /> repeat
                    </span>
                  )}
                  {t.needs_reply && (
                    <span className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-[#DC2626] px-1.5 py-0.5 text-[10px] font-bold text-white" title="Customer replied — awaiting your answer" data-testid={`needs-reply-${t.ticket_id}`}>
                      <MessagesSquare className="h-3 w-3" /> replied
                    </span>
                  )}
                  {t.notes?.length > 0 && (
                    <span className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-black px-1.5 py-0.5 text-[10px] font-bold text-white" title={`${t.notes.length} internal note(s)`} data-testid={`note-count-${t.ticket_id}`}>
                      <MessageSquareText className="h-3 w-3" /> {t.notes.length}
                    </span>
                  )}
                  <span className="block text-xs text-zinc-500">{fmtDate(t.created_at)}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="font-semibold">{t.name}</span>
                  <span className="block text-xs text-zinc-500">{t.phone}</span>
                </td>
                <td className="max-w-[280px] px-4 py-3">
                  <span className="block truncate text-xs font-bold uppercase tracking-wider text-zinc-500">{t.product}</span>
                  <span className="block truncate font-medium">{t.query}</span>
                  {t.offline_interest && (
                    <span className="mt-0.5 inline-flex items-center gap-1 text-xs font-bold text-[#B45309]"><MapPin className="h-3 w-3" /> Indore</span>
                  )}
                </td>
                <td className="px-4 py-3"><Pill kind="qrc" value={t.qrc_type} /></td>
                <td className="px-4 py-3"><Pill kind="priority" value={t.priority} /></td>
                <td className="px-4 py-3"><Pill kind="status" value={t.status} /></td>
                <td className="px-4 py-3">
                  <Pill kind="level" value={t.escalation_level} className={alarmRow(t) ? "alarm-pulse" : ""} />
                  <span className="block text-xs text-zinc-500">{t.elapsed_working_hours}h worked</span>
                </td>
                <td className="px-4 py-3"><AssignButton ticket={t} user={user} onAssign={onAssign} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="space-y-3 md:hidden" data-testid="tickets-cards">
        {tickets.map((t) => (
          <article
            key={t.id}
            onClick={() => onOpen(t)}
            className={`rounded-xl border-2 border-black bg-white p-4 ${alarmRow(t) ? "border-[#DC2626] bg-[#FEF2F2]" : ""}`}
            data-testid={`ticket-card-${t.ticket_id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <span className="flex items-center gap-2 font-mono text-lg font-bold">{box(t)}{t.ticket_id}</span>
              <Pill kind="level" value={t.escalation_level} className={alarmRow(t) ? "alarm-pulse" : ""} />
            </div>
            <p className="mt-2 font-semibold">{t.name} <span className="text-zinc-500">· {t.phone}</span></p>
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t.product}</p>
            <p className="text-sm">{t.query}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              <Pill kind="status" value={t.status} />
              <Pill kind="priority" value={t.priority} />
              <Pill kind="qrc" value={t.qrc_type} />
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-zinc-500">
              <span>{fmtDate(t.created_at)}</span>
              <AssignButton ticket={t} user={user} onAssign={onAssign} />
            </div>
          </article>
        ))}
      </div>
    </>
  );
}
